import styled, { css } from 'styled-components';
import colors from './colors';

type ColorsI = typeof colors;
type ColorType = keyof ColorsI;

interface BaseProps {
  color?: ColorType;
}

const getColorByProps: (props: BaseProps) => string = ({ color }) =>
  `color: ${color ? colors[color] : colors.black};`;

const baseTyping = css`
  font-family: 'Work Sans', sans-serif;
  font-style: normal;
`;

export const StyledH1 = styled.h1<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-weight: 500;
  font-size: 48px;
  line-height: 56px;
`;

export const StyledH2 = styled.h2<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-weight: 600;
  font-size: 32px;
  line-height: 48px;
`;

export const StyledParagraphLarge = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-weight: normal;
  font-size: 24px;
  line-height: 40px;
`;

export const StyledParagraph = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-weight: 300;
  font-size: 18px;
  line-height: 32px;
`;

export const StyledParagraphSmall = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
`;

export const StyledFormInput = styled.input<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  border: none;
  text-align: left;
  background-color: inherit;
  opacity: 0.7;
  border-bottom: 2px solid ${colors.lightGrey};
  &::placeholder {
    ${baseTyping}
    ${(props) => getColorByProps(props)}
  }
  &:hover {
    opacity: 1;
    border-bottom: 2px solid ${colors.grey};
  }
  &:focus {
    opacity: 1;
    color: ${colors.brand};
    border-bottom: 3px solid ${colors.brand};
    ::placeholder {
      color: ${colors.brand} !important;
    }
  }
`;

export const StyledParagraphHightlighted = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
`;

const buttonStyles = css`
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;

  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export interface ButtonProps extends BaseProps {
  margin?: 'left' | 'right' | 'both' | 'none' | string;
  customMargin?: string;
}

const getButtonMarginByProps: (props: ButtonProps) => string = ({
  margin = 'both',
  customMargin,
}) => {
  if (customMargin) {
    return `margin: ${margin}`;
  }
  switch (margin) {
    case 'left':
      return 'margin: 12px 0 12px 12px;';
    case 'right':
      return 'margin: 12px 12px 12px 0;';
    case 'none':
      return 'margin: 12px 0;';
    case 'both':
    default:
      return 'margin: 12px;';
  }
};

export const StyledAnchor = styled.a<ButtonProps>`
  ${baseTyping}
  ${buttonStyles}

  ${(props) => getColorByProps(props)}
  ${(props) => getButtonMarginByProps(props)}

  cursor: pointer;
`;

export const StyledButton = styled.button<ButtonProps>`
  ${baseTyping}
  ${buttonStyles}

  ${(props) => getColorByProps(props)}
  ${(props) => getButtonMarginByProps(props)}
`;

export const StyledHint = styled.small<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
`;

const typings = {
  StyledH1,
  StyledH2,
  StyledParagraphLarge,
  StyledParagraph,
  StyledParagraphSmall,
  StyledParagraphHightlighted,
  StyledAnchor,
  StyledButton,
  StyledHint,
  StyledFormInput,
};

export default typings;
